import { ConverterDefinition } from '../types';
import { convertCategoryCsv } from './categoryConverter';
import { convertFileWithAPI } from './converterApi';
import { fetchAllBanks, fetchBankConfig, flattenBanks } from './banksApi';

// Converter function registry
export const CONVERTER_FUNCTIONS = {
  convertCategoryCsv,
  convertFileWithAPI
};

class ConverterRegistry {
  private converterCache: Map<string, ConverterDefinition> = new Map();

  // Load basic converter list (minimal data) for UI display - now from API
  async loadConverterList(): Promise<{ id: string; name: string; description: string; logo?: string }[]> {
    try {
      // Fetch all banks from API and flatten
      const allBanksGrouped = await fetchAllBanks();
      const allBanks = flattenBanks(allBanksGrouped);
      
      // Return minimal data for display
      return allBanks.map(bank => ({
        id: bank.id,
        name: bank.name,
        description: bank.description,
        logo: bank.logo
      }));
    } catch (error) {
      console.error('Failed to load converter list:', error);
      throw new Error('Failed to load available converters');
    }
  }

  // Load full converter configuration on-demand when selected - now from API
  async loadConverterConfig(converterId: string): Promise<ConverterDefinition> {
    try {
      // Check cache first
      if (this.converterCache.has(converterId)) {
        return this.converterCache.get(converterId)!;
      }

      // Fetch bank config from API
      const bank = await fetchBankConfig(converterId);

      // Convert to ConverterDefinition format
      const converter: ConverterDefinition = {
        id: bank.id,
        name: bank.name,
        description: bank.description,
        logo: bank.logo,
        converterFunction: bank.converterFunction,
        fileTypes: bank.fileTypes,
        fileConfig: bank.fileConfig
      };

      // Cache the converter for future use
      this.converterCache.set(converterId, converter);
      
      return converter;
    } catch (error) {
      console.error(`Failed to load converter config for ${converterId}:`, error);
      throw new Error(`Failed to load configuration for ${converterId}`);
    }
  }

  // Future API method that will replace the JSON file approach
  async loadConverterConfigFromAPI(converterId: string): Promise<ConverterDefinition> {
    try {
      // This is how it will work in the future:
      const response = await fetch(`/api/converters/${converterId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch converter: ${response.statusText}`);
      }
      
      const converter: ConverterDefinition = await response.json();
      
      // Cache the converter
      this.converterCache.set(converterId, converter);
      
      return converter;
    } catch (error) {
      console.error(`Failed to load converter from API: ${converterId}`, error);
      throw error;
    }
  }

  // Get converter function by name
  getConverterFunction(functionName: string): Function | undefined {
    return (CONVERTER_FUNCTIONS as any)[functionName];
  }

  // Clear cache (useful for refreshing configurations)
  clearCache() {
    this.converterCache.clear();
  }

  // Get cached converter (if available)
  getCachedConverter(converterId: string): ConverterDefinition | undefined {
    return this.converterCache.get(converterId);
  }
}

// Singleton instance
export const converterRegistry = new ConverterRegistry();

// Helper function for future database/API integration
export const getConverterConfig = async (converterId: string): Promise<ConverterDefinition> => {
  return await converterRegistry.loadConverterConfig(converterId);
}; 